import { useState } from 'react';
import { ImageOff, Paperclip } from 'lucide-react';

/**
 * An email attachment, opened in Dossier (#321): its headers, and its body as
 * the sender formatted it. The HTML runs in a sandboxed frame with no
 * scripts and a Content-Security-Policy that loads nothing from the internet
 * until you choose "Show external images" - the same choice Outlook offers,
 * and what keeps tracking pixels quiet. Images that came inside the email
 * were turned into data: URIs on the server, so they always show.
 */

export interface EmailData {
  subject: string;
  from: string;
  to: string[];
  cc: string[];
  date: string;
  text: string;
  html: string | null;
  attachments: Array<{ name: string; mime: string; size: number }>;
}

export function emailSrcDoc(html: string, allowRemote: boolean): string {
  const img = allowRemote ? 'data: https: http:' : 'data:';
  const csp = `default-src 'none'; img-src ${img}; style-src 'unsafe-inline'; font-src data:;`;
  return `<!doctype html><html><head><meta charset="utf-8">`
    + `<meta http-equiv="Content-Security-Policy" content="${csp}">`
    + '<base target="_blank">'
    + '<style>body{margin:12px;font-family:Segoe UI,Calibri,Arial,sans-serif;font-size:14px;color:#1a1d2e;background:#fff;word-wrap:break-word}img{max-width:100%;height:auto}</style>'
    + `</head><body>${html}</body></html>`;
}

/** Whether the HTML would load an image from the internet. */
export function hasRemoteImages(html: string): boolean {
  return /<img\b[^>]*\bsrc\s*=\s*["']?\s*https?:/i.test(html)
    || /url\(\s*["']?\s*https?:/i.test(html)
    || /\bbackground\s*=\s*["']?\s*https?:/i.test(html);
}

const sizeLabel = (n: number) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const dateLabel = (iso: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

/**
 * `onOpenAttachment(i)` makes each of the email's own attachments a button
 * that opens attachment i (numbered as `email.attachments` lists them).
 */
export function EmailView({ email, onOpenAttachment }: { email: EmailData; onOpenAttachment?: (index: number) => void }) {
  const [remote, setRemote] = useState(false);
  const rows: Array<[string, string]> = [
    ['From', email.from],
    ['To', email.to.join(', ')],
    ['Cc', email.cc.join(', ')],
    ['Date', dateLabel(email.date)],
  ];
  return (
    <div className="flex min-h-0 flex-col gap-3">
      <div className="flex flex-col gap-1.5 border-b border-border pb-3">
        <h3 className="m-0 text-base font-semibold text-text-primary break-words">{email.subject || '(no subject)'}</h3>
        <dl className="m-0 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-sm">
          {rows.filter(([, v]) => v).map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-text-muted">{k}</dt>
              <dd className="m-0 text-text-secondary break-words">{v}</dd>
            </div>
          ))}
        </dl>
        {email.attachments.length > 0 && (
          <ul className="m-0 mt-1 flex list-none flex-wrap gap-1.5 p-0">
            {email.attachments.map((a, i) => {
              const label = <><Paperclip size={12} aria-hidden /><span>{a.name}</span><span className="text-text-muted">{sizeLabel(a.size)}</span></>;
              return (
                <li key={i} className="inline-flex">
                  {onOpenAttachment ? (
                    <button type="button" onClick={() => onOpenAttachment(i)} title={`Open ${a.name}`}
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-surface px-2 py-0.5 text-xs text-text-secondary hover:bg-bg-hover hover:text-text-primary">
                      {label}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-surface px-2 py-0.5 text-xs text-text-secondary">{label}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {email.html !== null ? (
        <>
          {!remote && hasRemoteImages(email.html) && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-bg-surface px-3 py-1.5 text-xs text-text-secondary">
              <ImageOff size={14} aria-hidden />
              <span className="flex-1">External images are blocked to protect your privacy.</span>
              <button type="button" onClick={() => setRemote(true)} className="rounded-md px-2 py-0.5 text-accent hover:bg-bg-hover">
                Show external images
              </button>
            </div>
          )}
          <iframe
            title="Email body"
            sandbox="allow-popups allow-popups-to-escape-sandbox"
            srcDoc={emailSrcDoc(email.html, remote)}
            className="h-[60vh] w-full rounded-md border border-border bg-white"
          />
        </>
      ) : (
        <pre className="m-0 max-h-[60vh] overflow-auto whitespace-pre-wrap break-words rounded-md border border-border bg-bg-surface p-3 font-sans text-sm text-text-primary">
          {email.text || '(empty message)'}
        </pre>
      )}
    </div>
  );
}
